import { getMainCropForNmaxGroup, isCropIgnoredForNutrition } from "@omnia/shared/nutrition"
import { CroppingGroupVm } from "../croppingGroup"
import { getDistinctValues, ICrop, ICropping, IOmniaSession, IOmniaSettings, labels, RegionId } from "../omnia"
import { ICroppingGrouping, ICroppingGroupItem } from "../types"
import { groupBy } from "@omnia/shared/utils/array"

export class CroppingGroupService {

    public groupings: ICroppingGrouping[]
    private _currentGrouping: ICroppingGrouping

    private settings: IOmniaSettings

    static $inject = ["OmniaSession"]

    constructor(private session: IOmniaSession) {

        this.settings = session.getSettings("nutrition")
        this.refresh()

    }

    get currentGrouping(): ICroppingGrouping {
        return this._currentGrouping
    }

    set currentGrouping(value: ICroppingGrouping) {
        this._currentGrouping = value || this.groupings[0]
        this.settings.set("croppingGrouping", this._currentGrouping.key)
    }

    public refresh() {

        let holding = this.session.holding
        let scotland = (holding.Region == RegionId.Scotland)

        this.groupings = [
            { text: "View all crops", key: "*", grouping: singleGroup },
            { text: "Group by crop", key: "crop", grouping: groupByCrop },
            { text: "Group by variety", key: "variety", grouping: groupByVariety },
            { text: "Group by crop and previous", key: "crop-previous", grouping: groupByCropAndPrevious },
            { text: "Group by N max group", key: "crop-nmax", grouping: (cropping => groupByNMaxGroup(cropping, scotland)) },
            { text: "Group by field group", key: "field-group", grouping: groupByFieldGroup },
            { text: "Group by description", key: "descr", grouping: groupByCropDescr },
        ]

        let initialGrouping = this.settings.get("croppingGrouping")

        this._currentGrouping = this.groupings.find(v => v.key == initialGrouping) || this.groupings[0]

    }

    public updateCroppingGroups<T extends ICroppingGroupItem>(cropping: T[], grouping?: ICroppingGrouping): CroppingGroupVm<T>[] {

        var grouping = (grouping ? grouping : this.currentGrouping)
        if (!grouping) grouping = this.groupings[0]

        this.currentGrouping = grouping

        this.settings.set("croppingGrouping", grouping ? grouping.key : null)

        return (grouping && grouping.grouping ? grouping.grouping(cropping) : null)

    }

    isUngrouped(): boolean {

        return (!this.currentGrouping) || (this.currentGrouping.key == "*")

    }


}

function singleGroup<T extends ICroppingGroupItem>(cropping: T[]): CroppingGroupVm<T>[] {

    var group = new CroppingGroupVm("*", cropping)
    group.visible = false

    return [group]

}

function groupByCrop<T extends ICroppingGroupItem>(cropping: T[]): CroppingGroupVm<T>[] {

    return createGroups(cropping, c => c.cropping.Crop, c => c.CropId, (crop, vm) => {

        vm.headerText = crop.Name
        vm.crop = crop

    })

}

function groupByCropDescr<T extends ICroppingGroupItem>(cropping: T[]): CroppingGroupVm<T>[] {

    return createGroups(cropping, c => c.cropping.CropDescr || "(No description set)", d => d, (descr, vm) => {

        vm.headerText = descr

        const crops = getDistinctValues(vm.cropping, c => c.crop, c => c?.CropId || 0)

        if (crops.length == 1) {
            vm.crop = crops[0]
        }

    })

}

function groupByFieldGroup<T extends ICroppingGroupItem>(cropping: T[]): CroppingGroupVm<T>[] {

    return createGroups(cropping, c => c.field.FieldGroup, g => g, (group, vm) => {

        vm.headerText = group || "(No group set)"
        vm.order = group ? 0 : 1

    })

}

function groupByCropAndPrevious<T extends ICroppingGroupItem>(cropping: T[]): CroppingGroupVm<T>[] {

    return createGroups(cropping,

        c => {

            var previous: ICrop = null

            var allPrevious = getPreviousCroppings([c.cropping])

            if (allPrevious && allPrevious.length) {
                var main = allPrevious.sort((p1, p2) => p2.Area - p1.Area)[0]
                previous = main.Crop
            }

            return {
                crop: c.cropping.Crop,
                previous: previous
            }

        },

        c => {
            return c.crop.CropId.toString() + (c.previous && c.previous.CropId ? ":" + c.previous.CropId.toString() : "")
        },

        (group, vm) => {

            vm.headerText = group.crop.Name + (group.previous ? " after " + group.previous.Name : "")
            vm.crop = group.crop

        })

}

function groupByVariety<T extends ICroppingGroupItem>(cropping: T[]): CroppingGroupVm<T>[] {

    return createGroups(cropping,

        c => { return { crop: c.cropping.Crop, variety: c.cropping.Variety } },

        c => {
            return c.crop.CropId.toString() + (c.variety ? ":" + c.variety.CropVarietyId.toString() : "")
        },

        (group, vm) => {

            vm.headerText = group.crop.Name + (group.variety ? " / " + group.variety.Name : "")
            vm.crop = group.crop

        }

    )

}

function groupByNMaxGroup<T extends ICroppingGroupItem>(cropping: T[], scotland: boolean): CroppingGroupVm<T>[] {

    return createGroups(

        // Source
        cropping,

        // Group selector
        c => {
            return {
                nmax: c.nmaxGroup && c.nmaxGroup.GroupId ? c.nmaxGroup : null,
                crop: c.nmaxGroup && c.nmaxGroup.GroupId ? null : c.crop
            }
        },

        // Group key selector
        g => {
            return g.nmax ? "g" + g.nmax.GroupId : "c" + g.crop.CropId
        },

        // Resulting group
        (group, vm) => {


            var nmax = group.nmax

            vm["nmax"] = nmax
            vm.groupInfoTemplate = "app/nutrition/nmax/nmaxGroupInfo.html"

            if (!nmax) {

                vm.headerText = group.crop.Name
                vm.order = 1
                vm.crop = group.crop

            }
            else {


                let group = vm.cropping.map(c => {
                    return { Crop: c.crop, Area: c.cropping.Area }
                })

                vm.crop = getMainCropForNmaxGroup(group)

                vm.headerText = scotland
                    ? vm.crop
                        ? (vm.crop.Grass ? "Grass" : vm.crop.Name)
                        : null
                    : labels.NMaxGroupId(nmax.GroupId)

                vm.order = 0


            }

        }

    )

}


function createGroups<TGroup, T extends ICroppingGroupItem>(
    cropping: T[],
    groupSelector: (c: T) => TGroup,
    groupKeySelector: (g: TGroup) => (number | string),
    groupInitializer: (g: TGroup, vm: CroppingGroupVm<T>) => void): CroppingGroupVm<T>[] {

    var cropped = (cropping || []).filter(c => !c.uncropped)

    var groups = groupBy(cropped, groupSelector, groupKeySelector)

    var vm = groups.map(g => {

        var croppingGroup = g.values

        var group = new CroppingGroupVm(String(groupKeySelector(g.key)), croppingGroup)

        groupInitializer(g.key, group)

        group.headerText = `${group.headerText} (${group.area} ha)`

        return group

    })

    vm = sortGroups(vm)

    return vm

}

function sortGroups<T extends ICroppingGroupItem>(groups: CroppingGroupVm<T>[]) {

    groups = groups.sort((g1, g2) => {

        let r = g1.order - g2.order
        if (r) return r

        if (g1.crop && g2.crop) {
            let i1 = isCropIgnoredForNutrition(g1.crop.CropId) ? 1 : 0
            let i2 = isCropIgnoredForNutrition(g2.crop.CropId) ? 1 : 0
            r = i1 - i2
            if (r) return r
        }

        return (g1.headerText || "").localeCompare(g2.headerText || "")

    })

    return groups

}

function getPreviousCroppings(croppings: ICropping[]) {

    var previous: ICropping[] = []
    var coverCrops: ICropping[] = []

    croppings.forEach(c => {
        var targets = c.Destroyed ? coverCrops : previous
        Array.prototype.push.apply(targets, c.PreviousCroppings)
    })

    if (coverCrops.length) {
        Array.prototype.push.apply(previous, getPreviousCroppings(coverCrops))
    }

    return previous

}
